import type {
  AdminAuditResponse,
  DrawingsResponse,
  WorkspacesResponse,
} from "../backend/backendApi";
import { selectCurrentUser } from "../state/editorStore";
import type { EditorShellState, SessionUser } from "@dxf-ai-editor/contracts";
import { t } from "../i18n";

type PersistentSliceResult = PromiseSettledResult<
  WorkspacesResponse | DrawingsResponse | AdminAuditResponse
>;

export type WorkspaceLikeFallback = WorkspacesResponse["data"][number];
export type DrawingLikeFallback = DrawingsResponse["data"][number];
export type AuditLikeFallback = AdminAuditResponse["data"][number];

export function buildLocalWorkspaceFallback(
  state: EditorShellState,
  currentUser: SessionUser,
): WorkspaceLikeFallback {
  return {
    id: "local-shell-workspace",
    name: state.drawing.title,
    slug: "local-shell",
    serverAlias: "local-preview",
    deploymentMode: "single_user_preview",
    visibility: "private",
    ownerEmail: currentUser.email,
    memberCount: state.session.users.length,
  };
}

export function buildLocalDrawingFallback(
  state: EditorShellState,
  currentUser: SessionUser,
): DrawingLikeFallback {
  return {
    id: "local-shell-drawing",
    name: state.drawing.title,
    slug: "local-shell-drawing",
    workspaceSlug: "local-shell",
    createdByEmail: currentUser.email,
    sourceFormat: "local_shell",
    sourceKey: state.drawing.id,
    status: "preview",
    lastAuditedAt: null,
  };
}

export function buildLocalAuditFallback(
  state: EditorShellState,
): AuditLikeFallback[] {
  const currentUser = selectCurrentUser(state);

  return state.audit.events.slice(0, 5).map((event, index) => ({
    id: `local-${event.id}`,
    workspaceSlug: "local-shell",
    drawingSlug: state.drawing.id,
    userEmail: currentUser.email,
    userRole: currentUser.role,
    command: event.commandType,
    alias: event.commandType.toLowerCase().replace(/[^a-z0-9]+/giu, "-"),
    hotkey: null,
    commandSource: event.source,
    spaceContext: state.drawing.activeSpaceId,
    entityIds: [],
    beforeSummary: null,
    afterSummary: event.summary,
    status: event.tone === "warning" ? "warning" : "success",
    occurredAt: `local-${index}`,
  }));
}

export function buildPersistentSliceErrorMessage(
  apiBaseUrl: string,
  results: Array<PersistentSliceResult | null>,
): string | undefined {
  const failures = results
    .filter(
      (result): result is PromiseRejectedResult =>
        result !== null &&
        result.status === "rejected" &&
        !isExpectedAuthBoundaryError(result.reason),
    )
    .map((result) =>
      result.reason instanceof Error
        ? result.reason.message
        : String(result.reason),
    );

  if (failures.length === 0) {
    return undefined;
  }

  return t("feed.dxf_error", {
    url: apiBaseUrl,
    failures: failures.join("; "),
  });
}

export function isExpectedAuthBoundaryError(reason: unknown): boolean {
  const message = reason instanceof Error ? reason.message : String(reason);

  return /\bstatus (401|403)\b/i.test(message);
}
